const squaredDistance = (a, b) => {
  if (a == null || b == null) {
    return NaN
  }
  const dl = a.l - b.l
  const da = a.a - b.a
  const db = a.b - b.b
  return dl * dl + da * da + db * db
}

const reverseGamma = (c) => {
  if (c <= 0.04045) return c / 12.92
  return Math.pow((c + 0.055) / 1.055, 2.4)
}

const labfunc = (t) => {
  const d3 = 0.0088564
  const d2_3 = 0.1284185
  if (t > d3) {
    return Math.pow(t, 1 / 3)
  }
  return t / d2_3 + 0.137931
}

class Rules {
  constructor(neutrals, options = {}) {
    this.neutrals = neutrals.map(this.parseColor)

    this.options = {
      sameColorThreshold: 500,
      brightnessContrastThreshold: 16,
      usesSchoolColorsRank: 1,
      hasComplementaryColorsRank: 0.3,
      brightnessStrategyRank: 1,
      contrastStrategyRank: 2,
      ...options
    }
  }

  parseColor(hexstr) {
    const parsed = parseInt(hexstr, 16)
    if (isNaN(parsed)) {
      return null
    }

    // Convert from sRGB to CIELAB.
    // Based on formulas from https://en.wikipedia.org/wiki/SRGB and
    // https://en.wikipedia.org/wiki/CIELAB_color_space
    const r = ((parsed >> 16) & 0xff) / 0xff
    const g = ((parsed >> 8) & 0xff) / 0xff
    const b = (parsed & 0xff) / 0xff

    const rl = reverseGamma(r)
    const gl = reverseGamma(g)
    const bl = reverseGamma(b)

    const x = rl * 0.4124 + gl * 0.3576 + bl * 0.1805
    const y = rl * 0.2126 + gl * 0.7152 + bl * 0.0722
    const z = rl * 0.0193 + gl * 0.1192 + bl * 0.9504

    const xd65 = x / 0.950489
    const yd65 = y / 1
    const zd65 = z / 1.08884

    const ret = {
      rgb: hexstr,
      l: 116 * labfunc(yd65) - 16,
      a: 500 * (labfunc(xd65) - labfunc(yd65)),
      b: 200 * (labfunc(yd65) - labfunc(zd65))
    }

    ret.hue = Math.atan2(ret.b, ret.a)
    ret.chroma = Math.sqrt(ret.a * ret.a + ret.b * ret.b)

    return ret
  }

  /* Parses a string of whitespace-separated key/value pairs, where
   * each value is a comma-separated set of number pairs separated by
   * '&'. e.g. "contrast:2&1,3&0 positives: 0&1"
   */
  parseColoringStrategy(strategy) {
    if (!strategy) {
      return null
    }

    const ret = {}

    const splits = strategy.split(/\s/)
    for (const i of splits) {
      const s = i.split(':')
      if (s.length != 2) {
        return null
      }

      const pairs = s[1].split(',')
      ret[s[0]] = []

      for (const pair of pairs) {
        const splitPair = pair.split('&')
        if (splitPair.length == 2) {
          const first = parseInt(splitPair[0], 10)
          const second = parseInt(splitPair[1], 10)
          if (!isNaN(first) && !isNaN(second)) {
            ret[s[0]].push([first, second])
          }
        }
      }
    }

    return ret
  }

  // Returns the color from 'colors' that has the highest contrast to
  // 'c'. If 'threshold' is defined, returns null if the highest contrasting
  // color doesn't exceed it.
  mostContrastingColor(c, colors, threshold) {
    let maxDistance = 0
    let bestColor
    for (const sc of colors) {
      const d = squaredDistance(sc, c)
      if (d > maxDistance) {
        maxDistance = d
        bestColor = sc
      }
    }
    if (threshold == undefined || maxDistance > threshold) {
      return bestColor
    }
    return null
  }

  selectBestSingleColor(pc, schoolColors) {
    for (const sc of schoolColors) {
      if (Math.abs(sc.l - pc.l) >= this.options.brightnessContrastThreshold) {
        return sc
      }
    }

    return this.mostContrastingColor(pc, this.neutrals)
  }

  rankPair(pair, strategy, pc, schools) {
    const ranks = {
      schoolColors: 0,
      brightness: 0,
      contrast: 0,
      complementary: 0,
      rank: 0
    }

    const {
      sameColorThreshold,
      brightnessContrastThreshold,
      usesSchoolColorsRank,
      hasComplementaryColorsRank,
      brightnessStrategyRank,
      contrastStrategyRank
    } = this.options
    // Rank based on how many of the school colors are used in the product or
    // design. The primary school color gets the biggest boost in rank; each
    // subsequent color is half as important.
    if (usesSchoolColorsRank) {
      for (
        let i = 0, r = usesSchoolColorsRank;
        i < schools.length;
        i++, r /= 2
      ) {
        for (let j = 0; j < 3; j++) {
          // Allow a wider range of product colors to qualify as a school color, but
          // require that design colors match exactly.
          const threshold = j == 0 ? sameColorThreshold : 0
          const dist = squaredDistance(schools[i], pair[j])
          if (dist <= threshold) {
            ranks.rank += r

            // Give a small tie-breaking penalty the farther the school color
            // is down the list. But the penalty is half as significant for each
            // subsequent school color.
            ranks.rank -= j / (1 << i) / 10000
            break
          }
        }
      }
    }
    ranks.schoolColors = ranks.rank

    // Rank based on how complementary the colors are.
    // Calculate the magnitude of the sum of all the color vectors in this pair.
    // Complimentary colors will add to zero, so give the rank a penalty based
    // on how far from zero the resulting sum is.
    if (hasComplementaryColorsRank) {
      const suma = pair[0].a + pair[1].a + pair[2].a
      const sumb = pair[0].b + pair[1].b + pair[2].b
      const mag = suma * suma + sumb * sumb
      if (mag < sameColorThreshold) {
        ranks.complementary += hasComplementaryColorsRank
        ranks.rank += hasComplementaryColorsRank
      }
    }

    if (strategy == null) {
      return ranks
    }

    // Rank based on the brightness rules. Give a boost for each strategy the
    // pair satisfies.
    const brightnesses = pair[3] ? strategy.negatives : strategy.positives
    if (brightnesses && brightnessStrategyRank) {
      for (let strat of brightnesses) {
        if (pair[strat[0]].l > pair[strat[1]].l) {
          ranks.brightness += brightnessStrategyRank
          ranks.rank += brightnessStrategyRank
        }
      }
    }

    // Rank based on the contrast rules.
    if (contrastStrategyRank && strategy.contrast) {
      let minBrightnessContrast = Infinity
      for (let strat of strategy.contrast) {
        const c1 = pair[strat[0]]
        const c2 = pair[strat[1]]
        minBrightnessContrast = Math.min(
          minBrightnessContrast,
          Math.abs(c1.l - c2.l)
        )
      }
      if (minBrightnessContrast > brightnessContrastThreshold) {
        ranks.rank += contrastStrategyRank
        ranks.contrast += contrastStrategyRank
      }
      // Give a tie-breaking boost to the pairs with the highest minimum contrast.
      ranks.rank += minBrightnessContrast / 1000
      ranks.contrast += minBrightnessContrast / 1000
    }
    return ranks
  }

  selectBestTwoColors(pc, strategy, hasNegativeDesign, schoolColors) {
    let schoolSet = new Set()
    let schools = [...schoolColors]
    schoolColors.forEach((c) => schoolSet.add(c.rgb.toLowerCase()))
    for (const n of this.neutrals) {
      if (!schoolSet.has(n.rgb.toLowerCase())) {
        schools.push(n)
      }
    }
    let pairs = []
    for (let i = 0; i < schools.length; i++) {
      for (let j = 0; j < schools.length; j++) {
        pairs.push([pc, schools[i], schools[j], false])
        if (hasNegativeDesign) {
          pairs.push([pc, schools[i], schools[j], true])
        }
      }
    }
    pairs.forEach(
      (p) => (p.ranks = this.rankPair(p, strategy, pc, schoolColors))
    )
    pairs.sort((a, b) => b.ranks.rank - a.ranks.rank)
    return pairs
  }

  parseAndSortProductColors(productColors, schoolColors) {
    schoolColors = schoolColors.map(this.parseColor)
    const ret = productColors.map(this.parseColor)
    const {sameColorThreshold} = this.options

    for (let i = 0; i < ret.length; i++) {
      ret[i].distanceToSchool1 = squaredDistance(ret[i], schoolColors[0])
      ret[i].distanceToSchool2 = squaredDistance(ret[i], schoolColors[1])
    }

    ret.sort((a, b) => {
      // Prefer the first school color...
      if (
        a.distanceToSchool1 < sameColorThreshold &&
        b.distanceToSchool1 < sameColorThreshold
      ) {
        return a.distanceToSchool1 - b.distanceToSchool1
      }
      if (a.distanceToSchool1 < sameColorThreshold) {
        return -1
      }
      if (b.distanceToSchool1 < sameColorThreshold) {
        return 1
      }
      // Then the second school color...
      if (
        a.distanceToSchool2 < sameColorThreshold &&
        b.distanceToSchool2 < sameColorThreshold
      ) {
        return a.distanceToSchool2 - b.distanceToSchool2
      }
      if (a.distanceToSchool2 < sameColorThreshold) {
        return -1
      }
      if (b.distanceToSchool2 < sameColorThreshold) {
        return 1
      }
      // Then prefer the least saturated non-school colors.
      return a.chroma - b.chroma
    })
    return ret
  }

  /*
   * All rules for a given school
   */
  rules(productColor, schoolColors, design = {}, hasNegativeDesign = false) {
    const {NoOfColors, ColoringStrategy, DesignLocations, ProcessorClass} =
      design
    const strategy = this.parseColoringStrategy(ColoringStrategy)

    schoolColors = schoolColors.map(this.parseColor)
    const pc = this.parseColor(productColor)

    hasNegativeDesign =
      DesignLocations && DesignLocations.find((d) => d.Negative)

    // A few designs have belong to the Foo2Color set of classes, but have
    // NoOfColors set to 1. The old server ignores NoOfColors and forces
    // these to be 2-color designs.
    let numColors = /2Color/i.test(ProcessorClass) ? 2 : NoOfColors

    if (numColors > 1) {
      const c = this.selectBestTwoColors(
        pc,
        strategy,
        hasNegativeDesign,
        schoolColors
      )

      return c.map((r) => {
        let designColors = [r[1].rgb, r[2].rgb]

        // The legacy imageserver only ever sets two colors and
        // sets any remaining to white.
        designColors.fill('f8f8f8', 2, numColors)
        return {
          productColor: pc.rgb,
          designColors,
          negative: r[3],
          ranks: r.ranks
        }
      })
    }
    const c = this.selectBestSingleColor(pc, schoolColors)
    return [
      {
        productColor: pc.rgb,
        designColors: [c.rgb],
        negative: hasNegativeDesign && pc.l < c.l
      }
    ]
  }
}
const NEUTRALS = ['f8f8f8', 'c4c4c4', 'aaaaaa', '181717']
const rules = new Rules(NEUTRALS)

export const sanitizeColorName = (color) =>
  color.toLowerCase().replace(/[\s/]/g, '_')

export const generateRules = (
  productColor,
  schoolColors,
  design,
  hasNegative
) => rules.rules(productColor, schoolColors, design, hasNegative)

export const sortProductColors = (productColors, schoolColors) =>
  rules.parseAndSortProductColors(productColors, schoolColors).map((c) => c.rgb)
